import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

/* Public shareable Weekly Executive Report — print-friendly (PDF via browser print). */
const API = process.env.REACT_APP_BACKEND_URL;

export default function WeeklyReportPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetch(`${API}/api/pixel/report/${token}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('not found'))))
      .then(setD)
      .catch(() => setErr('Report not found'));
  }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-gray-500">{err}</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading…</div>;

  const w = d.week;
  const accent = d.restaurant.primary_color || '#F77F11';
  const stats = [
    ['Visitors', w.visitors], ['WhatsApp', w.whatsapp], ['Calls', w.calls],
    ['Reservations', w.reservations], ['Orders', w.orders], ['Directions', w.directions],
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 print:bg-white" data-testid="weekly-report">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-gray-200 p-8 print:border-0">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {d.restaurant.logo_url && <img src={d.restaurant.logo_url} alt="" className="w-11 h-11 rounded-xl object-cover" />}
            <div>
              <h1 className="text-[20px] font-bold text-gray-900">{d.restaurant.name}</h1>
              <p className="text-[12px] text-gray-400">Weekly Executive Report · {w.from} → {w.to}</p>
            </div>
          </div>
          <button onClick={() => window.print()} data-testid="report-print-btn"
            className="print:hidden rounded-full text-white font-bold text-[12px] px-4 py-2" style={{ background: accent }}>
            Download PDF
          </button>
        </div>

        <div className="mt-6 rounded-2xl p-6 text-center" style={{ background: `${accent}12` }}>
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>Estimated business generated this week</p>
          <p className="mt-1 text-[40px] font-bold tabular-nums text-gray-900" data-testid="report-revenue">
            ${Number(w.est_revenue).toLocaleString()}
          </p>
          {w.trend_pct != null && (
            <p className={`text-[13px] font-semibold ${w.trend_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {w.trend_pct >= 0 ? '▲' : '▼'} {Math.abs(w.trend_pct)}% visitors vs previous week
            </p>
          )}
        </div>

        <div className="mt-5 grid grid-cols-3 sm:grid-cols-6 gap-3">
          {stats.map(([l, v]) => (
            <div key={l} className="rounded-xl border border-gray-100 p-3 text-center">
              <p className="text-[19px] font-bold tabular-nums">{v}</p>
              <p className="text-[9.5px] uppercase tracking-wider text-gray-400 font-bold">{l}</p>
            </div>
          ))}
        </div>

        {(d.channels || []).length > 0 && (
          <div className="mt-6">
            <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-2">Where guests came from</p>
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400">
                  <th className="pb-1.5">Channel</th><th className="pb-1.5 text-right">Visits</th>
                  <th className="pb-1.5 text-right">WhatsApp</th><th className="pb-1.5 text-right">Reservations</th>
                  <th className="pb-1.5 text-right">Orders</th><th className="pb-1.5 text-right">Est. revenue</th>
                </tr>
              </thead>
              <tbody>
                {d.channels.map((c) => (
                  <tr key={c.channel} className="border-t border-gray-100 text-[13px]">
                    <td className="py-1.5 capitalize font-medium text-gray-800">{c.channel}</td>
                    <td className="py-1.5 text-right tabular-nums">{c.visits}</td>
                    <td className="py-1.5 text-right tabular-nums">{c.whatsapp}</td>
                    <td className="py-1.5 text-right tabular-nums">{c.reservations}</td>
                    <td className="py-1.5 text-right tabular-nums">{c.orders}</td>
                    <td className="py-1.5 text-right tabular-nums font-semibold" style={{ color: accent }}>${Number(c.est_revenue).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-8 text-[10.5px] text-gray-400 text-center">
          {d.source} · Generated {d.generated_at?.slice(0, 10)} · Powered by <b>Kitxens</b> — Restaurant Operating Intelligence
        </p>
      </div>
    </div>
  );
}
